import { cloneElement, createContext, isValidElement, useContext, useMemo, useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';

import type { Placement } from '@floating-ui/react';

import { WithRef } from 'support/types/withRef';

type TooltipOptions = {
  placement?: Placement;
  spacing?: number;
};

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export function Tooltip({
  children,
  enabled = true,
  ...options
}: { children: React.ReactNode; enabled?: boolean } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return (
    <TooltipContext.Provider value={{ ...tooltip, open: enabled ? tooltip.open : false }}>
      {children}
    </TooltipContext.Provider>
  );
}

type ElementRef = HTMLElement;

// Define the props interface
type TooltipTriggerProps = {
  children: React.ReactElement<any, string | React.JSXElementConstructor<any>> & {
    ref?: React.RefObject<ElementRef>;
  };
} & Omit<React.HTMLProps<ElementRef>, 'children'>;

export const TooltipTrigger = ({ children, ref: propRef, ...props }: WithRef<TooltipTriggerProps, HTMLElement>) => {
  const context = useTooltipContext();
  const childrenRef = children.props.ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  if (!isValidElement(children)) {
    return null;
  }

  const { ref: _childRef, ...childrenProps } = children.props;

  return cloneElement(
    children,
    context.getReferenceProps({
      ref,
      ...props,
      ...childrenProps,
      'data-state': context.open ? 'open' : 'closed',
    }),
  );
};

export const TooltipContent = ({
  ref: propRef,
  ...props
}: WithRef<React.HTMLProps<HTMLDivElement>, HTMLDivElement>) => {
  const context = useTooltipContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!context.open) {
    return null;
  }

  return (
    <FloatingPortal>
      <div
        ref={ref}
        className="absolute z-100 mb-3 max-w-60 rounded-sm bg-black px-2 py-1 text-xs text-white"
        style={context.floatingStyles}
        {...context.getFloatingProps(props)}
      />
    </FloatingPortal>
  );
};

function useTooltip({ placement = 'top', spacing = 5 }: TooltipOptions = {}) {
  const [open, setOpen] = useState(false);

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(spacing),
      flip({
        fallbackAxisSideDirection: 'start',
        padding: spacing,
      }),
      shift({ padding: spacing }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
    enabled: true,
  });
  const focus = useFocus(context, {
    enabled: true,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data],
  );
}

const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};
